import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import { getCompanyGroups } from '@/lib/api/company/configuration/adminPanel/companies/groups/getCompanyGroups'
import { createCompanyGroup } from '@/lib/api/company/configuration/adminPanel/companies/groups/createCompanyGroup'
import { renameCompanyGroup } from '@/lib/api/company/configuration/adminPanel/companies/groups/renameCompanyGroup'
import { addCompaniesToGroup } from '@/lib/api/company/configuration/adminPanel/companies/groups/addCompaniesToGroup'
import { removeCompaniesFromGroup } from '@/lib/api/company/configuration/adminPanel/companies/groups/removeCompaniesFromGroup'
import { deleteCompanyGroups } from '@/lib/api/company/configuration/adminPanel/companies/groups/deleteCompanyGroups'
import { getCompaniesInGroup } from '@/lib/api/company/configuration/adminPanel/companies/groups/getCompaniesInGroup'
import type { CompanyGroupDto } from '@/lib/api/company/configuration/adminPanel/companies/groups/types'

const useCompanyGroupService = () => {
  const startLoading = useLoadingStore((s) => s.startLoading)
  const getGroups = async ({ companyId }: { companyId: string }): Promise<CompanyGroupDto[]> => {
    const stopLoading = startLoading()
    try {
      const res = await getCompanyGroups({ companyId })
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  const addGroup = async ({
    companyId,
    name,
  }: {
    companyId: string
    name: string
  }): Promise<CompanyGroupDto | null> => {
    const stopLoading = startLoading()
    try {
      const res = await createCompanyGroup({
        companyId,
        payload: { name },
      })
      return res.data
    } catch (err) {
      return null
    } finally {
      stopLoading()
    }
  }

  const changeGroupName = async ({
    companyId,
    groupId,
    newName,
  }: {
    companyId: string
    groupId: number
    newName: string
  }) => {
    const stopLoading = startLoading()
    try {
      await renameCompanyGroup({
        companyId,
        payload: { groupId, newName },
      })
      return true
    } catch (err) {
      return false
    } finally {
      stopLoading()
    }
  }

  const removeGroups = async ({
    companyId,
    groupIds,
  }: {
    companyId: string
    groupIds: number[]
  }) => {
    const stopLoading = startLoading()
    try {
      await deleteCompanyGroups({
        companyId,
        payload: { groupIds },
      })
      return true
    } catch (err) {
      return false
    } finally {
      stopLoading()
    }
  }

  const addCompanyToGroup = async ({
    companyId,
    groupId,
    companies,
  }: {
    companyId: string
    groupId: number
    companies: string[]
  }) => {
    const stopLoading = startLoading()
    try {
      await addCompaniesToGroup({
        companyId,
        payload: { companyIds: companies, groupId },
      })
      return true
    } catch (err) {
      return false
    } finally {
      stopLoading()
    }
  }

  const removeCompanyFromGroup = async ({
    companyId,
    groupId,
    companies,
  }: {
    companyId: string
    groupId: number
    companies: string[]
  }) => {
    const stopLoading = startLoading()
    try {
      await removeCompaniesFromGroup({
        companyId,
        payload: { companyIds: companies, groupId },
      })
      return true
    } catch (err) {
      return false
    } finally {
      stopLoading()
    }
  }

  const getCompaniesInGroupById = async ({
    companyId,
    groupId,
  }: {
    companyId: string
    groupId: number
  }): Promise<string[]> => {
    const stopLoading = startLoading()
    try {
      const res = await getCompaniesInGroup({ companyId, groupId })
      return res.data
    } catch (err) {
      return []
    } finally {
      stopLoading()
    }
  }

  return {
    getGroups,
    addGroup,
    changeGroupName,
    removeGroups,
    addCompanyToGroup,
    removeCompanyFromGroup,
    getCompaniesInGroupById,
  }
}

export default useCompanyGroupService
